/**
 * EstimateEditor Component
 * Extracted from App.js for better maintainability
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { openAuthenticatedPdf } from '../utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Utility functions
const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getProvinceTaxSummary = (province) => {
  const rates = {
    'AB': { rate: 5, components: [{ name: 'GST', rate: 5 }] },
    'BC': { rate: 12, components: [{ name: 'GST', rate: 5 }, { name: 'PST', rate: 7 }] },
    'MB': { rate: 12, components: [{ name: 'GST', rate: 5 }, { name: 'PST', rate: 7 }] },
    'NB': { rate: 15, components: [{ name: 'HST', rate: 15 }] },
    'NL': { rate: 15, components: [{ name: 'HST', rate: 15 }] },
    'NS': { rate: 15, components: [{ name: 'HST', rate: 15 }] },
    'NT': { rate: 5, components: [{ name: 'GST', rate: 5 }] },
    'NU': { rate: 5, components: [{ name: 'GST', rate: 5 }] },
    'ON': { rate: 13, components: [{ name: 'HST', rate: 13 }] },
    'PE': { rate: 15, components: [{ name: 'HST', rate: 15 }] },
    'QC': { rate: 14.975, components: [{ name: 'GST', rate: 5 }, { name: 'QST', rate: 9.975 }] },
    'SK': { rate: 11, components: [{ name: 'GST', rate: 5 }, { name: 'PST', rate: 6 }] },
    'YT': { rate: 5, components: [{ name: 'GST', rate: 5 }] }
  };
  return rates[province] || rates['ON'];
};

// HtmlPreviewModal component (inline for self-containment)
const HtmlPreviewModal = ({ title, url, onClose, testId }) => {
  if (!url) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-start sm:items-center justify-center z-50 p-2 pt-12 sm:p-4 sm:pt-4 overflow-y-auto" data-testid={testId || 'html-preview-modal'}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[85vh] sm:h-[86vh] flex flex-col my-auto sm:my-0">
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200">
          <h3 className="text-sm sm:text-lg font-semibold text-gray-800 truncate" data-testid={`${testId || 'html-preview-modal'}-title`}>{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-1" data-testid={`${testId || 'html-preview-modal'}-close`}>✕</button>
        </div>
        <div className="flex-1 bg-gray-100 overflow-auto">
          <iframe src={url} title={title} className="w-full h-full border-0" style={{ minHeight: '100%' }} key={url} data-testid={`${testId || 'html-preview-modal'}-iframe`} />
        </div>
      </div>
    </div>
  );
};

const EstimateEditor = ({ estimate, onBack, onSave, onCreateInvoice, onDuplicate }) => {
  const getDefaultExpirationDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState(estimate || {
    client_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    client_city: '',
    client_province: 'ON',
    client_postal_code: '',
    date: new Date().toISOString().split('T')[0],
    expiration_date: getDefaultExpirationDate(),
    po_number: '',
    status: 'DRAFT',
    line_items: [],
    subtotal: 0,
    markup_type: 'percent',
    markup_percentage: 0,
    markup_amount: 0,
    discount_type: 'percent',
    discount_percentage: 0,
    discount_amount: 0,
    tax_rate: 13.0,
    tax_rate_manual_override: false,
    tax_amount: 0,
    total: 0,
    notes: '',
    contract: '2025 Revised Terms and Conditions',
    private_notes: '',
    created_by: '',
    photos: [],
    documents: []
  });

  const [isSaving, setIsSaving] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [contractTemplates, setContractTemplates] = useState([]);
  const [showContractSelector, setShowContractSelector] = useState(false);
  const [salesSettings, setSalesSettings] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [isDraggingPhotos, setIsDraggingPhotos] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showSignatureWarning, setShowSignatureWarning] = useState(false);
  const [signatureLockActive, setSignatureLockActive] = useState(Boolean(estimate?.client_signature));
  const [sendForm, setSendForm] = useState({ recipient_email: '', message: '', request_type: 'full', request_value: '', attach_pdf: true, cc_admin: true });
  const [isDeleting, setIsDeleting] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomerKey, setSelectedCustomerKey] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [previewToken, setPreviewToken] = useState('');
  const [previewEstimateNumber, setPreviewEstimateNumber] = useState('');
  const [expandedLineItems, setExpandedLineItems] = useState({});
  const [autoSaveState, setAutoSaveState] = useState('idle');
  const autoSaveTimerRef = useRef(null);
  const skipAutoSaveRef = useRef(false);

  // Load items and contracts from library
  useEffect(() => {
    const loadLibraryData = async () => {
      try {
        const [itemsRes, contractsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/sales/items`),
          axios.get(`${API}/sales/contracts`),
          axios.get(`${API}/sales/settings`)
        ]);
        setLibraryItems(itemsRes.data);
        setContractTemplates(contractsRes.data);
        setSalesSettings(settingsRes.data);
        
        // Set default contract if creating new estimate
        if (!estimate && contractsRes.data.length > 0) {
          const defaultContract = contractsRes.data.find(c => c.is_default);
          if (defaultContract) {
            setFormData(prev => ({ ...prev, contract: defaultContract.content }));
          }
        }
      } catch (error) {
        console.error('Error loading library data:', error);
      }
    };
    loadLibraryData();
  }, []);

  useEffect(() => {
    const loadCustomerOptions = async () => {
      try {
        const [leadsRes, intakesRes, clientsRes] = await Promise.allSettled([
          axios.get(`${API}/crm/leads`),
          axios.get(`${API}/intakes`),
          axios.get(`${API}/sales/clients`)
        ]);
        const leadsData = leadsRes.status === 'fulfilled' ? (leadsRes.value.data || []) : [];
        const intakesData = intakesRes.status === 'fulfilled' ? (intakesRes.value.data || []) : [];
        const clientsData = clientsRes.status === 'fulfilled' ? (clientsRes.value.data || []) : [];
        const leadOptions = leadsData.map((lead) => ({
          key: `crm:${lead.id}`,
          label: `${lead.name || lead.email || 'CRM Lead'}${lead.email ? ` • ${lead.email}` : ''}`,
          source: 'CRM',
          data: lead
        }));
        const intakeOptions = intakesData.map((intake) => ({
          key: `intake:${intake.id}`,
          label: `${intake.name || intake.email || 'Intake'}${intake.email ? ` • ${intake.email}` : ''}`,
          source: 'Intake',
          data: intake
        }));
        const clientOptions = clientsData.map((client) => ({
          key: `client:${client.id}`,
          label: `${client.name || client.email || 'Client'}${client.email ? ` • ${client.email}` : ''}`,
          source: 'Client',
          data: client
        }));
        // Deduplicate: prefer Client > CRM > Intake, keyed by email or name
        const all = [...clientOptions, ...leadOptions, ...intakeOptions];
        const seen = new Set();
        const unique = all.filter(opt => {
          const dedupKey = (opt.data.email || opt.data.name || '').toLowerCase().trim();
          if (!dedupKey || seen.has(dedupKey)) return false;
          seen.add(dedupKey);
          return true;
        });
        setCustomerOptions(unique);
      } catch (error) {
        console.error('Error loading customer options:', error);
      }
    };
    loadCustomerOptions();
  }, []);

  useEffect(() => {
    if (!selectedCustomerKey && formData.crm_lead_id && customerOptions.length) {
      const match = customerOptions.find((option) => option.source === 'CRM' && option.data.id === formData.crm_lead_id);
      if (match) {
        setSelectedCustomerKey(match.key);
      }
    }
  }, [customerOptions, formData.crm_lead_id, selectedCustomerKey]);

  useEffect(() => {
    if (!selectedCustomerKey) {
      setCustomerSearch('');
      return;
    }
    const match = customerOptions.find((option) => option.key === selectedCustomerKey);
    if (match) {
      setCustomerSearch(match.label);
    }
  }, [selectedCustomerKey, customerOptions]);

  useEffect(() => {
    setSignatureLockActive(Boolean(estimate?.client_signature));
  }, [estimate?.id]);

  // Calculate totals whenever line items or adjustments change
  useEffect(() => {
    calculateTotals();
  }, [formData.line_items, formData.markup_percentage, formData.markup_type, formData.discount_percentage, formData.discount_type, formData.tax_rate]);

  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce((sum, item) => {
      const itemTotal = (item.quantity || 0) * (item.unit_cost || 0);
      const markupAmount = itemTotal * ((item.markup_percentage || 0) / 100);
      return sum + itemTotal + markupAmount;
    }, 0);

    const markupType = formData.markup_type || 'percent';
    const markupAmount = markupType === 'flat'
      ? (formData.markup_percentage || 0)
      : subtotal * ((formData.markup_percentage || 0) / 100);
    const afterMarkup = subtotal + markupAmount;
    const discountType = formData.discount_type || 'percent';
    const discountAmount = discountType === 'flat'
      ? (formData.discount_percentage || 0)
      : afterMarkup * ((formData.discount_percentage || 0) / 100);
    const afterDiscount = afterMarkup - discountAmount;
    const taxAmount = afterDiscount * (formData.tax_rate / 100);
    const total = afterDiscount + taxAmount;

    setFormData(prev => ({
      ...prev,
      subtotal,
      markup_amount: markupAmount,
      discount_amount: discountAmount,
      tax_amount: taxAmount,
      total
    }));
  };

  const getProvinceTaxSummary = (province) => {
    const components = salesSettings?.province_tax_rates?.[province] || [];
    if (components.length > 0) {
      const totalRate = components.reduce((sum, component) => sum + (parseFloat(component.rate) || 0), 0);
      return { components, totalRate };
    }
    const fallbackRate = salesSettings?.tax_rates?.[province] || 0;
    return {
      components: fallbackRate ? [{ name: 'Tax', rate: fallbackRate }] : [],
      totalRate: fallbackRate
    };
  };

  useEffect(() => {
    if (!salesSettings) return;
    if (formData.tax_rate_manual_override) return;
    const { totalRate } = getProvinceTaxSummary(formData.client_province);
    setFormData(prev => ({ ...prev, tax_rate: totalRate }));
  }, [salesSettings, formData.client_province, formData.tax_rate_manual_override]);

  const addLineItem = (sectionName = 'Description') => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, {
        id: Date.now().toString(),
        section_name: sectionName,
        description: '',
        quantity: 1,
        unit_cost: 0,
        markup_percentage: 0,
        total: 0
      }]
    }));
  };

  const addItemFromLibrary = (libraryItem) => {
    const itemDescription = libraryItem.description ? `${libraryItem.name}\n${libraryItem.description}` : libraryItem.name;
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, {
        id: Date.now().toString(),
        section_name: libraryItem.name,
        description: itemDescription,
        quantity: 1,
        unit_cost: libraryItem.base_cost,
        markup_percentage: libraryItem.default_markup_percentage,
        total: 0
      }]
    }));
    setShowItemSelector(false);
  };

  const updateLineItem = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index][field] = value;
    setFormData(prev => ({ ...prev, line_items: newLineItems }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const toggleLineItemExpanded = (itemId) => {
    setExpandedLineItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleSelectExistingCustomer = (value) => {
    setSelectedCustomerKey(value);
    const selected = customerOptions.find((option) => option.key === value);
    if (!selected) {
      return;
    }
    const sourceData = selected.source === 'CRM'
      ? (selected.data.intake_snapshot || selected.data)
      : selected.data;
    setFormData(prev => ({
      ...prev,
      client_name: sourceData.name || selected.data.name || '',
      client_email: sourceData.email || selected.data.email || '',
      client_phone: sourceData.phone || selected.data.phone || '',
      client_address: sourceData.address || selected.data.address || '',
      client_city: sourceData.city || selected.data.city || '',
      client_province: sourceData.province || selected.data.province || '',
      client_postal_code: sourceData.postal_code || selected.data.postal_code || '',
      client_id: selected.source === 'Client' ? selected.data.id : (prev.client_id || ''),
      crm_lead_id: selected.source === 'CRM'
        ? selected.data.id
        : (selected.data.crm_lead_id || prev.crm_lead_id || '')
    }));
  };

  const handleSave = async (status = formData.status || 'DRAFT', options = {}) => {
    const { silent = false, refresh = true } = options;
    if (!formData.client_name) {
      if (!silent) {
        alert('Please enter a client name');
      }
      return;
    }

    setIsSaving(true);
    try {
      const dataToSave = { ...formData, status };
      const estimateId = formData.id || estimate?.id;
      if (estimateId) {
        await axios.put(`${API}/sales/estimates/${estimateId}`, dataToSave);
        setFormData(prev => ({ ...prev, status }));
        if (!silent) {
          alert('Estimate updated successfully!');
        }
      } else {
        const response = await axios.post(`${API}/sales/estimates`, dataToSave);
        if (response?.data) {
          setFormData(response.data);
        }
        if (!silent) {
          alert('Estimate created successfully!');
        }
      }
      if (refresh) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving estimate:', error);
      if (!silent) {
        alert('Error saving estimate. Please try again.');
      }
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusUpdate = async (nextStatus) => {
    try {
      await handleSave(nextStatus, { silent: true, refresh: true });
      alert(`Estimate marked as ${nextStatus.toLowerCase()}.`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Unable to update estimate status.');
    }
  };

  const autoSaveEstimate = async () => {
    if (isSaving) return;
    const hasContent = Boolean(
      formData.client_name ||
      formData.client_email ||
      formData.client_phone ||
      formData.notes ||
      formData.private_notes ||
      formData.po_number ||
      (formData.photos && formData.photos.length > 0) ||
      (formData.documents && formData.documents.length > 0) ||
      (formData.line_items && formData.line_items.some(item => item.description || item.quantity || item.unit_cost))
    );
    if (!hasContent) return;
    setAutoSaveState('saving');
    try {
      const estimateId = formData.id || estimate?.id;
      const dataToSave = { ...formData, status: formData.status || 'DRAFT' };
      if (estimateId) {
        await axios.put(`${API}/sales/estimates/${estimateId}`, dataToSave);
      } else {
        const response = await axios.post(`${API}/sales/estimates`, dataToSave);
        if (response?.data) {
          skipAutoSaveRef.current = true;
          setFormData(response.data);
        }
      }
      setAutoSaveState('idle');
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveState('error');
    }
  };

  useEffect(() => {
    if (skipAutoSaveRef.current) {
      skipAutoSaveRef.current = false;
      return;
    }
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveEstimate();
    }, 1200);
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData]);

  const openSendModal = () => {
    setSendForm({
      recipient_email: formData.client_email || '',
      message: salesSettings?.estimate_email_message || '',
      attach_pdf: false
    });
    setShowSendModal(true);
  };

  const handleRequestSignedEdit = () => {
    setShowSignatureWarning(true);
  };

  const handleDiscardSignedEdit = () => {
    setShowSignatureWarning(false);
  };

  const handleClearSignaturesAndEdit = () => {
    setFormData(prev => ({
      ...prev,
      client_signature: '',
      client_signature_name: '',
      client_signature_ip: '',
      approved_at: null,
      status: prev.status === 'APPROVED' ? 'ISSUED' : prev.status
    }));
    setSignatureLockActive(false);
    setShowSignatureWarning(false);
  };

  const handleDuplicateAndEdit = async () => {
    await handleDuplicateEstimate();
    setSignatureLockActive(false);
    setShowSignatureWarning(false);
  };

  const handleSendEstimate = async () => {
    if (!sendForm.recipient_email) {
      alert('Recipient email is required.');
      return;
    }

    setIsSending(true);
    try {
      let estimateId = formData.id || estimate?.id;
      const currentStatus = formData.status || estimate?.status || 'DRAFT';
      const statusForSend = currentStatus === 'DRAFT' ? 'ISSUED' : currentStatus;

      if (!estimateId) {
        const response = await axios.post(`${API}/sales/estimates`, { ...formData, status: statusForSend });
        estimateId = response.data.id;
      } else {
        await axios.put(`${API}/sales/estimates/${estimateId}`, { ...formData, status: statusForSend });
      }

      await axios.post(`${API}/sales/estimates/${estimateId}/send`, {
        ...sendForm,
        base_url: window.location.origin
      });
      alert('Estimate sent successfully.');
      setShowSendModal(false);
      onSave();
    } catch (error) {
      console.error('Error sending estimate:', error);
      const detail = error?.response?.data?.detail;
      alert(detail || 'Unable to send estimate. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleCreateInvoiceFromEstimate = async () => {
    try {
      let estimateId = formData.id || estimate?.id;
      if (!estimateId) {
        const response = await axios.post(`${API}/sales/estimates`, { ...formData, status: formData.status || 'ISSUED' });
        estimateId = response.data.id;
      }
      const invoiceRes = await axios.post(`${API}/sales/estimates/${estimateId}/invoice`);
      if (onCreateInvoice) {
        onCreateInvoice(invoiceRes.data);
      }
    } catch (error) {
      console.error('Error creating invoice from estimate:', error);
      alert('Unable to create invoice from this estimate.');
    }
  };

  const handleDuplicateEstimate = async () => {
    try {
      const estimateId = formData.id || estimate?.id;
      if (!estimateId) {
        alert('Please save this estimate before duplicating.');
        return;
      }
      const response = await axios.post(`${API}/sales/estimates/${estimateId}/duplicate`);
      const duplicate = response.data;
      setFormData(duplicate);
      if (onDuplicate) {
        onDuplicate(duplicate);
      }
      alert(`Estimate duplicated as #${duplicate.estimate_number}.`);
    } catch (error) {
      console.error('Error duplicating estimate:', error);
      alert('Unable to duplicate this estimate.');
    }
  };

  const handleOpenEstimatePdf = async () => {
    const estimateId = formData.id || estimate?.id;
    if (estimateId) {
      openAuthenticatedPdf(`${API}/sales/estimates/${estimateId}/pdf`);
      return;
    }
    try {
      const response = await axios.post(`${API}/sales/estimates`, { ...formData, status: formData.status || 'DRAFT' });
      if (response?.data?.id) {
        setFormData(response.data);
        openAuthenticatedPdf(`${API}/sales/estimates/${response.data.id}/pdf`);
      }
    } catch (error) {
      alert('Unable to generate PDF.');
    }
  };

  const ensureEstimateSaved = async () => {
    if (formData.id) return formData.id;
    const response = await axios.post(`${API}/sales/estimates`, { ...formData, status: formData.status || 'DRAFT' });
    setFormData(response.data);
    return response.data.id;
  };

  const handlePhotoUpload = async (event) => {
    try {
      const files = Array.from(event.target.files || event.dataTransfer?.files || []);
      if (files.length === 0) return;
      // Filter to images only
      const imageFiles = files.filter(f => f.type.startsWith('image/'));
      if (imageFiles.length === 0) return;
      if ((formData.photos?.length || 0) + imageFiles.length > 20) {
        alert('Maximum 20 photos per estimate.');
        return;
      }
      setIsUploadingPhotos(true);
      const estimateId = await ensureEstimateSaved();
      
      // Send all files in a single request (backend supports List[UploadFile])
      const formDataUpload = new FormData();
      imageFiles.forEach(file => formDataUpload.append('files', file));
      
      const response = await axios.post(`${API}/sales/estimates/${estimateId}/photos`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      
      setFormData(prev => ({ ...prev, photos: response.data.photos }));
      if (event.target?.value) event.target.value = '';
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Unable to upload photos. Please try again.');
    } finally {
      setIsUploadingPhotos(false);
    }
  };

  const handlePhotoDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingPhotos(false);
    handlePhotoUpload(event);
  };

  const handlePhotoDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingPhotos(true);
  };

  const handlePhotoDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingPhotos(false);
  };

  // ---- Document handlers ----
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);
  const [isDraggingDocs, setIsDraggingDocs] = useState(false);

  const handleDocUpload = async (event) => {
    try {
      const files = Array.from(event.target.files || event.dataTransfer?.files || []);
      if (files.length === 0) return;
      if ((formData.documents?.length || 0) + files.length > 20) {
        alert('Maximum 20 documents per estimate.');
        return;
      }
      setIsUploadingDocs(true);
      const estimateId = await ensureEstimateSaved();
      const fd = new FormData();
      files.forEach(file => fd.append('files', file));
      const response = await axios.post(`${API}/sales/estimates/${estimateId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setFormData(prev => ({ ...prev, documents: response.data.documents }));
      if (event.target?.value) event.target.value = '';
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert('Unable to upload documents. Please try again.');
    } finally {
      setIsUploadingDocs(false);
    }
  };

  const handleRemoveDocument = async (docId) => {
    if (!confirm('Remove this document?')) return;
    try {
      const estimateId = formData.id || estimate?.id;
      const response = await axios.delete(`${API}/sales/estimates/${estimateId}/documents/${docId}`);
      setFormData(prev => ({ ...prev, documents: response.data.documents }));
    } catch (error) {
      console.error('Error removing document:', error);
    }
  };

  const handleDocDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingDocs(false);
    handleDocUpload(event);
  };

  const handleDocDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingDocs(true);
  };

  const handleDocDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingDocs(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocIcon = (filename) => {
    const ext = (filename || '').split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return 'PDF';
    if (['doc', 'docx'].includes(ext)) return 'DOC';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'XLS';
    if (['ppt', 'pptx'].includes(ext)) return 'PPT';
    if (['txt', 'rtf'].includes(ext)) return 'TXT';
    if (['zip', 'rar', '7z'].includes(ext)) return 'ZIP';
    return 'FILE';
  };

  const handleRemovePhoto = async (photoId) => {
    try {
      if (!formData.id) return;
      const response = await axios.delete(`${API}/sales/estimates/${formData.id}/photos/${photoId}`);
      setFormData(prev => ({ ...prev, photos: response.data.photos }));
    } catch (error) {
      console.error('Error removing photo:', error);
      alert('Unable to remove photo.');
    }
  };

  const handlePreviewEstimate = async () => {
    try {
      let estimateId = formData.id || estimate?.id;
      let token = formData.public_token;
      let estimateNumber = formData.estimate_number || estimate?.estimate_number || '';
      if (!token) {
        estimateId = await ensureEstimateSaved();
        const response = await axios.get(`${API}/sales/estimates/${estimateId}`);
        token = response.data?.public_token;
        estimateNumber = response.data?.estimate_number || estimateNumber;
        setFormData(response.data);
      }
      if (!token) {
        throw new Error('Preview link unavailable');
      }
      setPreviewToken(token);
      setPreviewEstimateNumber(estimateNumber);
    } catch (error) {
      console.error('Error opening preview:', error);
      alert('Unable to open preview.');
    }
  };

  const handleClosePreview = () => {
    setPreviewToken('');
    setPreviewEstimateNumber('');
  };

  const getPhotoUrl = (photoId) => `${API}/sales/estimates/photos/${photoId}`;

  const handleDeleteEstimate = async () => {
    if (!estimate?.id) return;
    if (!window.confirm('Delete this estimate? This cannot be undone.')) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/sales/estimates/${estimate.id}`);
      alert('Estimate deleted.');
      onSave();
    } catch (error) {
      console.error('Error deleting estimate:', error);
      alert('Unable to delete estimate.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBack = async () => {
    try {
      await autoSaveEstimate();
    } finally {
      onBack();
    }
  };

  const filteredCustomerOptions = customerOptions
    .filter((option) => option.label.toLowerCase().includes(customerSearch.toLowerCase()))
    .slice(0, 20);

  const estimateStatus = (formData.status || estimate?.status || 'DRAFT').toUpperCase();
  const estimateStatusLabel = estimateStatus.replace(/_/g, ' ');
  const estimateStatusClasses = {
    APPROVED: 'bg-emerald-100 text-emerald-700',
    DECLINED: 'bg-rose-100 text-rose-700',
    ISSUED: 'bg-blue-100 text-blue-700',
    SENT: 'bg-blue-100 text-blue-700',
    DRAFT: 'bg-gray-100 text-gray-600'
  }[estimateStatus] || 'bg-gray-100 text-gray-600';

  const renderEstimateActionsMenu = () => (
    <div className="absolute right-0 left-0 sm:left-auto mt-2 w-full sm:w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20" data-testid="estimate-actions-menu">
      <div className="py-2">
        <button
          onClick={() => {
            setShowActionsMenu(false);
            handleCreateInvoiceFromEstimate();
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          data-testid="estimate-actions-create-invoice"
        >
          Create Invoice
        </button>
        <button
          onClick={() => {
            setShowActionsMenu(false);
            handleDuplicateEstimate();
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          data-testid="estimate-actions-duplicate"
        >
          Duplicate
        </button>
        <button
          onClick={() => {
            setShowActionsMenu(false);
            openSendModal();
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          data-testid="estimate-actions-send"
        >
          Send Estimate
        </button>
        <button
          onClick={() => {
            setShowActionsMenu(false);
            handlePreviewEstimate();
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          data-testid="estimate-actions-preview"
        >
          Preview
        </button>
        <button
          onClick={async () => {
            setShowActionsMenu(false);
            handleOpenEstimatePdf();
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          data-testid="estimate-actions-pdf"
        >
          PDF
        </button>
        <button
          onClick={async () => {
            setShowActionsMenu(false);
            const estimateId = formData.id || estimate?.id;
            if (estimateId) {
              openAuthenticatedPdf(`${API}/sales/estimates/${estimateId}/work-order-pdf`);
            } else {
              try {
                const response = await axios.post(`${API}/sales/estimates`, { ...formData, status: formData.status || 'DRAFT' });
                if (response?.data?.id) {
                  setFormData(response.data);
                  openAuthenticatedPdf(`${API}/sales/estimates/${response.data.id}/work-order-pdf`);
                }
              } catch (error) {
                alert('Unable to generate work order.');
              }
            }
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          data-testid="estimate-actions-work-order"
        >
          Create Work Order
        </button>
        {estimate?.id && (
          <button
            onClick={() => {
              setShowActionsMenu(false);
              handleDeleteEstimate();
            }}
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            data-testid="estimate-actions-delete"
          >
            Delete
          </button>
        )}
      </div>
      <div className="border-t border-gray-200 py-2">
        <button
          onClick={() => {
            setShowActionsMenu(false);
            handleStatusUpdate('APPROVED');
          }}
          className="w-full text-left px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-50"
          data-testid="estimate-actions-approve"
        >
          Mark as Approved
        </button>
        <button
          onClick={() => {
            setShowActionsMenu(false);
            handleStatusUpdate('DECLINED');
          }}
          className="w-full text-left px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
          data-testid="estimate-actions-decline"
        >
          Mark as Declined
        </button>
        <button
          onClick={() => {
            setShowActionsMenu(false);
            handleStatusUpdate('ISSUED');
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          data-testid="estimate-actions-pending"
        >
          Mark as Pending
        </button>
      </div>
    </div>
  );

  const renderItemSelectorModal = () => (
    showItemSelector && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto" data-testid="estimate-item-selector-modal">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col my-auto sm:my-0">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800" data-testid="estimate-item-selector-title">Select Item from Library</h3>
              <button
                onClick={() => setShowItemSelector(false)}
                className="text-gray-600 hover:text-gray-800 text-2xl"
                data-testid="estimate-item-selector-close"
              >
                ×
              </button>
            </div>

            {/* Search Bar */}
            <input
              type="text"
              placeholder="Search items..."
              value={itemSearchTerm}
              onChange={(e) => setItemSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="estimate-item-selector-search"
            />
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {libraryItems.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">No items in library yet.</p>
                <p className="text-sm text-gray-500">Go to Settings → Item Library to create items.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {libraryItems
                  .filter(item => item.name.toLowerCase().includes(itemSearchTerm.toLowerCase()))
                  .map(libItem => (
                    <button
                      key={libItem.id}
                      onClick={() => addItemFromLibrary(libItem)}
                      className="w-full text-left border border-gray-300 rounded-lg p-4 hover:bg-blue-50 hover:border-blue-500 transition-colors"
                      data-testid={`estimate-item-selector-item-${libItem.id}`}
                    >
                      <h4 className="font-semibold text-gray-800">{libItem.name}</h4>
                    </button>
                  ))}
                {libraryItems.filter(item => item.name.toLowerCase().includes(itemSearchTerm.toLowerCase())).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No items found matching "{itemSearchTerm}"
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-6 border-t">
            <button
              onClick={() => {
                setShowItemSelector(false);
                setItemSearchTerm('');
              }}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium"
              data-testid="estimate-item-selector-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  );

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <div className="sales-editor-mobile-scale" data-testid="estimate-editor-scale-wrapper">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="sm:hidden">
            <div className="grid grid-cols-3 items-center gap-2">
              <button
                onClick={handleBack}
                className="text-emerald-600 font-semibold text-sm flex items-center gap-1"
                data-testid="estimate-editor-back-mobile"
              >
                ← Back
              </button>
              <div className="text-center text-base font-semibold text-gray-900" data-testid="estimate-editor-title-mobile">
                {estimate?.id ? `#${estimate.estimate_number}` : 'New'}
              </div>
              <div className="flex justify-end relative">
                <button
                  onClick={() => setShowActionsMenu((prev) => !prev)}
                  className="text-emerald-600 font-semibold text-sm flex items-center gap-1"
                  data-testid="estimate-actions-toggle"
                >
                  Actions ▾
                </button>
                {showActionsMenu && renderEstimateActionsMenu()}
              </div>
            </div>
            <div
              className={`text-xs mt-1 ${autoSaveState === 'error' ? 'text-red-600' : 'text-gray-500'}`}
              data-testid="estimate-autosave-status"
            >
              {autoSaveState === 'error' ? 'Save failed' : 'Auto-save on'}
            </div>
          </div>

          <div className="hidden sm:flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full">
              <button 
                onClick={handleBack}
                className="w-full sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium text-sm sm:text-base"
                data-testid="estimate-editor-back"
              >
                ← Back
              </button>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800" data-testid="estimate-editor-title">
                {estimate?.id ? `${signatureLockActive ? 'Estimate' : 'Edit Estimate'} #${estimate.estimate_number}` : 'New Estimate'}
              </h1>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
              <div
                className={`text-xs text-left sm:text-right ${autoSaveState === 'error' ? 'text-red-600' : 'text-gray-500'}`}
                data-testid="estimate-autosave-status"
              >
                {autoSaveState === 'error' ? 'Save failed' : 'Auto-save on'}
              </div>
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => setShowActionsMenu((prev) => !prev)}
                  className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white px-5 py-2 rounded-lg font-semibold flex items-center justify-center gap-2"
                  data-testid="estimate-actions-toggle"
                >
                  Actions ▾
                </button>
                {showActionsMenu && renderEstimateActionsMenu()}
              </div>
            </div>
          </div>
        </div>
      </div>
      {signatureLockActive && (
        <div className="bg-amber-50 border-b border-amber-200" data-testid="estimate-signed-banner">
          <div className="container mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="text-sm font-semibold text-amber-800" data-testid="estimate-signed-banner-text">
              Signed estimate — view only. Editing will clear signatures.
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handlePreviewEstimate}
                className="px-3 py-1.5 text-sm font-semibold text-amber-700 border border-amber-300 bg-white rounded-md"
                data-testid="estimate-signed-preview-button"
              >
                Preview
              </button>
              <button
                onClick={handleOpenEstimatePdf}
                className="px-3 py-1.5 text-sm font-semibold text-amber-700 border border-amber-300 bg-white rounded-md"
                data-testid="estimate-signed-pdf-button"
              >
                PDF
              </button>
              <button
                onClick={handleRequestSignedEdit}
                className="px-3 py-1.5 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-md"
                data-testid="estimate-signed-edit-button"
              >
                Edit Signed Estimate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 py-8 pb-32 sm:pb-8 max-w-5xl relative">
        {signatureLockActive && (
          <div
            className="absolute inset-0 z-20 cursor-not-allowed"
            onClick={handleRequestSignedEdit}
            data-testid="estimate-signed-overlay"
          />
        )}
        {renderItemSelectorModal()}

        <div className="sm:hidden space-y-4" data-testid="estimate-editor-mobile-layout">
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm" data-testid="estimate-mobile-summary-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500" data-testid="estimate-mobile-summary-label">Estimate</div>
                <div className="text-lg font-semibold text-gray-900" data-testid="estimate-mobile-summary-number">
                  {formData.estimate_number || estimate?.estimate_number || 'New Estimate'}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wide text-gray-500" data-testid="estimate-mobile-summary-total-label">Total</div>
                <div className="text-xl font-bold text-gray-900" data-testid="estimate-mobile-summary-total">${formData.total.toFixed(2)}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${estimateStatusClasses}`} data-testid="estimate-mobile-status-chip">
                {estimateStatusLabel}
              </span>
              <div
                className={`text-[11px] ${autoSaveState === 'error' ? 'text-red-600' : 'text-gray-500'}`}
                data-testid="estimate-mobile-summary-autosave"
              >
                {autoSaveState === 'error' ? 'Save failed' : 'Auto-save on'}
              </div>
            </div>
          </div>
          <div>
            <div className="bg-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-4 py-2 rounded-md" data-testid="estimate-mobile-client-header">Client</div>
            <div className="mt-2 space-y-2">
              <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
                <label className="text-xs font-semibold text-gray-500 uppercase">Search Existing Customer</label>
                <div className="relative mt-2">
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 150)}
                    className="w-full px-3 py-2 bg-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Search by name, email, or phone"
                    data-testid="estimate-existing-customer-search-mobile"
                  />
                  {showCustomerDropdown && (
                    <div
                      className="absolute z-20 mt-2 w-full max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
                      data-testid="estimate-existing-customer-dropdown-mobile"
                    >
                      {filteredCustomerOptions.length === 0 && (
                        <div className="px-4 py-3 text-sm text-gray-500" data-testid="estimate-existing-customer-empty-mobile">
                          No matches found.
                        </div>
                      )}
                      {filteredCustomerOptions.map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          onMouseDown={() => {
                            handleSelectExistingCustomer(option.key);
                            setSelectedCustomerKey(option.key);
                            setCustomerSearch(option.label);
                            setShowCustomerDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm"
                          data-testid={`estimate-existing-customer-option-mobile-${option.key.replace(/[^a-zA-Z0-9-]/g, '-')}`}
                        >
                          <div className="font-medium text-gray-800">{option.label}</div>
                          <div className="text-xs text-gray-500">{option.source === 'CRM' ? 'CRM Lead' : option.source === 'Client' ? 'Sales Client' : 'Customer Intake'}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 divide-y">
                <div className="flex items-center gap-2 px-4 py-3">
                  <span className="text-sm text-gray-600">Client</span>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                    className="flex-1 text-right text-sm font-semibold text-gray-900 bg-transparent focus:outline-none"
                    placeholder="Client Name"
                    data-testid="estimate-client-name-mobile"
                  />
                  <span className="text-gray-400">›</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-3">
                  <span className="text-sm text-gray-600">Email</span>
                  <input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                    className="flex-1 text-right text-sm text-gray-900 bg-transparent focus:outline-none"
                    placeholder="email@example.com"
                    data-testid="estimate-client-email-mobile"
                  />
                </div>
                <div className="flex items-center gap-2 px-4 py-3">
                  <span className="text-sm text-gray-600">Phone</span>
                  <input
                    type="tel"
                    value={formData.client_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                    className="flex-1 text-right text-sm text-gray-900 bg-transparent focus:outline-none"
                    placeholder="807-xxx-xxxx"
                    data-testid="estimate-client-phone-mobile"
                  />
                </div>
                <div className="flex items-center gap-2 px-4 py-3">
                  <span className="text-sm text-gray-600">Address</span>
                  <input
                    type="text"
                    value={formData.client_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_address: e.target.value }))}
                    className="flex-1 text-right text-sm text-gray-900 bg-transparent focus:outline-none"
                    placeholder="Street Address"
                    data-testid="estimate-client-address-mobile"
                  />
                </div>
                <div className="flex items-center gap-2 px-4 py-3">
                  <span className="text-sm text-gray-600">City</span>
                  <input
                    type="text"
                    value={formData.client_city}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_city: e.target.value }))}
                    className="flex-1 text-right text-sm text-gray-900 bg-transparent focus:outline-none"
                    placeholder="City"
                    data-testid="estimate-client-city-mobile"
                  />
                </div>
                <div className="flex items-center gap-2 px-4 py-3">
                  <span className="text-sm text-gray-600">Province</span>
                  <select
                    value={formData.client_province}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_province: e.target.value }))}
                    className="flex-1 text-right text-sm text-gray-900 bg-transparent focus:outline-none"
                    data-testid="estimate-client-province-mobile"
                  >
                    <option value="ON">Ontario</option>
                    <option value="AB">Alberta</option>
                    <option value="BC">British Columbia</option>
                    <option value="MB">Manitoba</option>
                    <option value="NB">New Brunswick</option>
                    <option value="NL">Newfoundland and Labrador</option>
                    <option value="NS">Nova Scotia</option>
                    <option value="PE">Prince Edward Island</option>
                    <option value="QC">Quebec</option>
                    <option value="SK">Saskatchewan</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 px-4 py-3">
                  <span className="text-sm text-gray-600">Postal</span>
                  <input
                    type="text"
                    value={formData.client_postal_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_postal_code: e.target.value }))}
                    className="flex-1 text-right text-sm text-gray-900 bg-transparent focus:outline-none"
                    placeholder="A1A 1A1"
                    data-testid="estimate-client-postal-mobile"
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-4 py-2 rounded-md flex items-center justify-between" data-testid="estimate-mobile-description-header">
              <span data-testid="estimate-line-items-title-mobile">Line Items ({formData.line_items.length})</span>
              <span className="text-[10px] text-gray-400" data-testid="estimate-line-items-hint-mobile">Swipe to delete</span>
            </div>
            <div className="mt-2 bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="estimate-mobile-line-items">
              {formData.line_items.map((item, index) => {
                const itemTitle = item.description?.split('\n')[0] || item.section_name || 'Line Item';
                const itemBadge = item.section_name && item.section_name !== itemTitle ? item.section_name : '';
                const itemDetails = item.description?.split('\n').slice(1).join(' ') || 'Tap to edit details';
                const itemTotal = ((item.quantity || 0) * (item.unit_cost || 0) * (1 + (item.markup_percentage || 0) / 100)).toFixed(2);
                const isExpanded = expandedLineItems[item.id];
                const quantityValue = item.quantity || 0;
                const unitCostValue = Number(item.unit_cost || 0).toFixed(2);
                const markupValue = item.markup_percentage || 0;
                return (
                  <div key={item.id} className="border-b border-gray-100 last:border-b-0" data-testid={`estimate-line-mobile-${index}`}>
                    <div className="overflow-x-auto" data-testid={`estimate-line-mobile-swipe-${index}`}>
                      <div className="flex w-[calc(100%+96px)]">
                        <button
                          type="button"
                          onClick={() => toggleLineItemExpanded(item.id)}
                          className="flex-1 flex items-center justify-between px-4 py-3 bg-white"
                          data-testid={`estimate-line-mobile-toggle-${index}`}
                        >
                          <div className="flex-1 min-w-0 text-left">
                            <div className="flex items-center gap-2">
                              <div className="font-semibold text-gray-900 truncate" data-testid={`estimate-line-mobile-title-${index}`}>{itemTitle}</div>
                              {itemBadge && (
                                <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid={`estimate-line-mobile-section-${index}`}>
                                  {itemBadge}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 truncate" data-testid={`estimate-line-mobile-summary-${index}`}>{itemDetails}</div>
                            <div className="mt-1 text-[11px] text-gray-400" data-testid={`estimate-line-mobile-meta-${index}`}>
                              {quantityValue} × ${unitCostValue} · Markup {markupValue}%
                            </div>
                          </div>
                          <div className="ml-3 text-right">
                            <div className="text-sm font-semibold text-gray-900" data-testid={`estimate-line-total-mobile-${index}`}>${itemTotal}</div>
                            <div className="text-xs text-gray-400">{isExpanded ? '▾' : '›'}</div>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          className="w-[96px] bg-red-600 text-white flex items-center justify-center text-xs font-semibold uppercase tracking-wide"
                          data-testid={`estimate-line-remove-mobile-${index}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-2 bg-gray-50 space-y-3">
                        <textarea
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[140px] text-sm"
                          rows="5"
                          placeholder="Item title on first line, details on next line"
                          data-testid={`estimate-line-description-mobile-${index}`}
                        />
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Quantity</label>
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              step="0.01"
                              data-testid={`estimate-line-quantity-mobile-${index}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Unit Cost ($)</label>
                            <input
                              type="number"
                              value={item.unit_cost}
                              onChange={(e) => updateLineItem(index, 'unit_cost', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              step="0.01"
                              data-testid={`estimate-line-unit-cost-mobile-${index}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Markup (%)</label>
                            <input
                              type="number"
                              value={item.markup_percentage}
                              onChange={(e) => updateLineItem(index, 'markup_percentage', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              step="0.1"
                              data-testid={`estimate-line-markup-mobile-${index}`}
                            />
                          </div>
                        </div>
                        <div className="text-right text-sm font-semibold text-gray-700" data-testid={`estimate-line-total-detail-mobile-${index}`}>
                          Total: ${itemTotal}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {formData.line_items.length === 0 && (
                <div className="px-4 py-6 text-sm text-gray-500" data-testid="estimate-line-items-empty-mobile">
                  No line items yet. Tap "Add Line Item" to get started.
                </div>
              )}
              <button
                type="button"
                onClick={() => addLineItem()}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-emerald-600"
                data-testid="estimate-line-add-mobile"
              >
                <span>+ Add Line Item</span>
                <span className="text-gray-400">›</span>
              </button>
              <button
                type="button"
                onClick={() => setShowItemSelector(true)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-blue-600 border-t border-gray-100"
                data-testid="estimate-line-library-mobile"
              >
                <span>📚 Select from Library</span>
                <span className="text-gray-400">›</span>
              </button>
            </div>
          </div>

          <div
            onDrop={handlePhotoDrop}
            onDragOver={handlePhotoDragOver}
            onDragLeave={handlePhotoDragLeave}
          >
            <div className="bg-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-4 py-2 rounded-md" data-testid="estimate-mobile-attachments-header">Attachments</div>
            <div className={`mt-2 bg-white rounded-xl border-2 overflow-hidden transition-colors ${isDraggingPhotos ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
              <label className="flex items-center justify-between px-4 py-3 cursor-pointer" data-testid="estimate-photos-upload-mobile">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-800">Add Photos</span>
                  {isUploadingPhotos && <span className="text-xs text-blue-600 animate-pulse">Uploading...</span>}
                </div>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{formData.photos?.length || 0}/20</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={isUploadingPhotos}
                />
              </label>
              {isUploadingPhotos && (
                <div className="px-4 pb-3">
                  <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 animate-pulse" style={{ width: '60%' }}></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Processing images... this may take a moment.</p>
                </div>
              )}
              {!isUploadingPhotos && formData.photos?.length === 0 ? (
                <div className="px-4 pb-4 text-sm text-gray-500">Tap above to add up to 20 photos.</div>
              ) : (
                <div className="px-4 pb-4 grid grid-cols-2 gap-3">
                  {formData.photos.map((photo) => (
                    <div key={photo.id} className="relative border border-gray-200 rounded-lg overflow-hidden" data-testid={`estimate-photo-mobile-${photo.id}`}>
                      <img
                        src={getPhotoUrl(photo.id)}
                        alt={photo.filename}
                        className="w-full h-24 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(photo.id)}
                        className="absolute top-2 right-2 bg-white bg-opacity-90 text-red-600 text-xs px-2 py-1 rounded"
                        data-testid={`estimate-photo-remove-mobile-${photo.id}`}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>


          {/* Documents (Mobile) */}
          <div
            onDrop={handleDocDrop}
            onDragOver={handleDocDragOver}
            onDragLeave={handleDocDragLeave}
          >
            <div className="bg-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-4 py-2 rounded-md">Documents</div>
            <div className={`mt-2 bg-white rounded-xl border-2 overflow-hidden transition-colors ${isDraggingDocs ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
              <label className="flex items-center justify-between px-4 py-3 cursor-pointer">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-800">Add Documents</span>
                  {isUploadingDocs && <span className="text-xs text-blue-600 animate-pulse">Uploading...</span>}
                </div>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{formData.documents?.length || 0}/20</span>
                <input type="file" multiple onChange={handleDocUpload} className="hidden" disabled={isUploadingDocs} />
              </label>
              {formData.documents?.length > 0 && (
                <div className="border-t border-gray-200 divide-y divide-gray-100">
                  {formData.documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between px-4 py-2">
                      <a href={`${API}/sales/estimates/documents/${doc.id}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 hover:underline truncate">
                        {doc.filename}
                      </a>
                      <button type="button" onClick={() => handleRemoveDocument(doc.id)} className="text-red-500 text-xs px-2">Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>


          <div>
            <div className="bg-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-4 py-2 rounded-md" data-testid="estimate-mobile-totals-header">Totals</div>
            <div className="mt-2 bg-white rounded-xl border border-gray-200 divide-y">
              <div className="flex justify-between px-4 py-3 text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-semibold text-gray-900" data-testid="estimate-subtotal-mobile">${formData.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <span>Markup</span>
                  <div className="flex border border-gray-300 rounded overflow-hidden text-xs">
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, markup_type: 'percent' }))} className={`px-2 py-0.5 ${(formData.markup_type || 'percent') !== 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="estimate-markup-type-percent-mobile">%</button>
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, markup_type: 'flat' }))} className={`px-2 py-0.5 ${formData.markup_type === 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="estimate-markup-type-flat-mobile">$</button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.markup_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, markup_percentage: parseFloat(e.target.value) || 0 }))}
                    className="w-16 px-2 py-1 bg-gray-100 rounded-md text-right"
                    step="0.01"
                    data-testid="estimate-markup-percentage-mobile"
                  />
                  <span className="font-semibold" data-testid="estimate-markup-amount-mobile">${formData.markup_amount.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-3 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <span>Discount</span>
                  <div className="flex border border-gray-300 rounded overflow-hidden text-xs">
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, discount_type: 'percent' }))} className={`px-2 py-0.5 ${(formData.discount_type || 'percent') !== 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="estimate-discount-type-percent-mobile">%</button>
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, discount_type: 'flat' }))} className={`px-2 py-0.5 ${formData.discount_type === 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="estimate-discount-type-flat-mobile">$</button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) || 0 }))}
                    className="w-16 px-2 py-1 bg-gray-100 rounded-md text-right"
                    step="0.01"
                    data-testid="estimate-discount-percentage-mobile"
                  />
                  <span className="font-semibold" data-testid="estimate-discount-amount-mobile">-${formData.discount_amount.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="text-gray-600">Tax (%)</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0, tax_rate_manual_override: true }))}
                    className="w-16 px-2 py-1 bg-gray-100 rounded-md text-right"
                    step="0.1"
                    data-testid="estimate-tax-rate-mobile"
                  />
                  <span className="font-semibold" data-testid="estimate-tax-amount-mobile">${formData.tax_amount.toFixed(2)}</span>
                </div>
              </div>
              <div className="px-4 py-2 text-[11px] text-gray-500" data-testid="estimate-tax-breakdown-mobile">
                Province taxes: {(getProvinceTaxSummary(formData.client_province).components || [])
                  .map(component => `${component.name} ${component.rate}%`).join(' + ') || 'N/A'}
              </div>
              <div className="flex justify-between px-4 py-3 text-sm font-semibold">
                <span className="text-gray-800">Total (CAD)</span>
                <span className="text-gray-900" data-testid="estimate-total-mobile">${formData.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-4 py-2 rounded-md" data-testid="estimate-mobile-details-header">Document Details</div>
            <div className="mt-2 bg-white rounded-xl border border-gray-200 divide-y">
              <div className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="text-gray-600">Estimate #</span>
                <span className="font-semibold text-gray-900" data-testid="estimate-number-mobile">{formData.estimate_number || estimate?.estimate_number || '—'}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="text-gray-600">Date</span>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="text-right bg-transparent focus:outline-none"
                  data-testid="estimate-date-mobile"
                />
              </div>
              <div className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="text-gray-600">Expiry</span>
                <input
                  type="date"
                  value={formData.expiration_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiration_date: e.target.value }))}
                  className="text-right bg-transparent focus:outline-none"
                  data-testid="estimate-expiration-date-mobile"
                />
              </div>
              <div className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="text-gray-600">PO Number</span>
                <input
                  type="text"
                  value={formData.po_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, po_number: e.target.value }))}
                  className="text-right bg-transparent focus:outline-none"
                  data-testid="estimate-po-number-mobile"
                />
              </div>
            </div>
          </div>

          <div>
            <div className="bg-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-4 py-2 rounded-md" data-testid="estimate-mobile-notes-header">Notes for Client</div>
            <div className="mt-2 bg-white rounded-xl border border-gray-200 p-3">
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="4"
                placeholder="Public notes visible to client"
                data-testid="estimate-notes-mobile"
              />
            </div>
          </div>

          <div>
            <div className="bg-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-4 py-2 rounded-md" data-testid="estimate-mobile-contract-header">Contract Terms</div>
            <div className="mt-2 bg-white rounded-xl border border-gray-200 p-3">
              {contractTemplates.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowContractSelector(!showContractSelector)}
                  className="mb-2 text-sm px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-300 rounded-lg font-medium transition-colors"
                  data-testid="estimate-contract-template-toggle-mobile"
                >
                  {showContractSelector ? 'Hide Templates' : 'Select Templates'}
                </button>
              )}

              {showContractSelector && contractTemplates.length > 0 && (
                <div className="mb-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <p className="text-sm text-gray-600 mb-3">Select one or more contract templates to add:</p>
                  <div className="space-y-2">
                    {contractTemplates.map(contract => (
                      <label key={contract.id} className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              const separator = formData.contract ? '\n\n---\n\n' : '';
                              setFormData(prev => ({
                                ...prev,
                                contract: prev.contract + separator + contract.content
                              }));
                            } else {
                              const updatedContract = formData.contract
                                .split('\n\n---\n\n')
                                .filter(section => section !== contract.content)
                                .join('\n\n---\n\n');
                              setFormData(prev => ({ ...prev, contract: updatedContract }));
                            }
                          }}
                          className="mt-1 w-4 h-4 text-blue-600"
                          data-testid={`estimate-contract-template-mobile-${contract.id}`}
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-800">{contract.name}</span>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{contract.content.substring(0, 100)}...</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <textarea
                value={formData.contract}
                onChange={(e) => setFormData(prev => ({ ...prev, contract: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="6"
                placeholder="Contract terms and conditions"
                data-testid="estimate-contract-text-mobile"
              />
            </div>
          </div>

          <div>
            <div className="bg-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-4 py-2 rounded-md" data-testid="estimate-mobile-private-notes-header">Private Notes</div>
            <div className="mt-2 bg-white rounded-xl border border-gray-200 p-3">
              <textarea
                value={formData.private_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, private_notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-yellow-50"
                rows="3"
                placeholder="Private notes (not visible to client)"
                data-testid="estimate-private-notes-mobile"
              />
            </div>
          </div>
        </div>

        <div className="hidden sm:block">

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{(salesSettings?.company_name || '').toUpperCase()}</h2>
            <p className="text-gray-600">{[salesSettings?.company_address, salesSettings?.company_city, salesSettings?.company_province, salesSettings?.company_postal_code, salesSettings?.company_country].filter(Boolean).join(', ')}</p>
            <p className="text-gray-600">{[salesSettings?.company_email, salesSettings?.company_website].filter(Boolean).join(' | ')}</p>
          </div>
        </div>

        {/* Client & Estimate Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Client Information</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search Existing Customer</label>
            <div className="relative">
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setShowCustomerDropdown(true);
                }}
                onFocus={() => setShowCustomerDropdown(true)}
                onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 150)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Start typing a name, email, or phone"
                data-testid="estimate-existing-customer-search"
              />
              {showCustomerDropdown && (
                <div
                  className="absolute z-20 mt-2 w-full max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
                  data-testid="estimate-existing-customer-dropdown"
                >
                  {filteredCustomerOptions.length === 0 && (
                    <div className="px-4 py-3 text-sm text-gray-500" data-testid="estimate-existing-customer-empty">
                      No matches found.
                    </div>
                  )}
                  {filteredCustomerOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onMouseDown={() => {
                        handleSelectExistingCustomer(option.key);
                        setSelectedCustomerKey(option.key);
                        setCustomerSearch(option.label);
                        setShowCustomerDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm"
                      data-testid={`estimate-existing-customer-option-${option.key.replace(/[^a-zA-Z0-9-]/g, '-')}`}
                    >
                      <div className="font-medium text-gray-800">{option.label}</div>
                      <div className="text-xs text-gray-500">{option.source === 'CRM' ? 'CRM Lead' : option.source === 'Client' ? 'Sales Client' : 'Customer Intake'}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Client Name"
              data-testid="estimate-client-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="email@example.com"
              data-testid="estimate-client-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.client_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="807-xxx-xxxx"
              data-testid="estimate-client-phone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={formData.client_address}
                onChange={(e) => setFormData(prev => ({ ...prev, client_address: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Street Address"
              data-testid="estimate-client-address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={formData.client_city}
                onChange={(e) => setFormData(prev => ({ ...prev, client_city: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="City"
              data-testid="estimate-client-city"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
              <select
                value={formData.client_province}
                onChange={(e) => setFormData(prev => ({ ...prev, client_province: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="estimate-client-province"
              >
                <option value="ON">Ontario</option>
                <option value="AB">Alberta</option>
                <option value="BC">British Columbia</option>
                <option value="MB">Manitoba</option>
                <option value="NB">New Brunswick</option>
                <option value="NL">Newfoundland and Labrador</option>
                <option value="NS">Nova Scotia</option>
                <option value="PE">Prince Edward Island</option>
                <option value="QC">Quebec</option>
                <option value="SK">Saskatchewan</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
              <input
                type="text"
                value={formData.client_postal_code}
                onChange={(e) => setFormData(prev => ({ ...prev, client_postal_code: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="A1A 1A1"
              data-testid="estimate-client-postal"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="estimate-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
              <input
                type="date"
                value={formData.expiration_date || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, expiration_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="estimate-expiration-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
              <input
                type="text"
                value={formData.po_number}
                onChange={(e) => setFormData(prev => ({ ...prev, po_number: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="PO Number"
              data-testid="estimate-po-number"
              />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg font-bold text-gray-800" data-testid="estimate-line-items-title">Line Items</h3>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={() => setShowItemSelector(true)}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium text-sm sm:text-base"
                data-testid="estimate-select-from-library"
              >
                📚 Select from Library
              </button>
              <button
                onClick={() => addLineItem()}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm sm:text-base"
                data-testid="estimate-add-line-item"
              >
                + Create New Item
              </button>
            </div>
          </div>



          <div className="sm:hidden space-y-3" data-testid="estimate-line-items-mobile">
            {formData.line_items.map((item, index) => {
              const itemTitle = item.description?.split('\n')[0] || 'Line Item';
              const itemDetails = item.description?.split('\n').slice(1).join(' ') || 'Tap to edit details';
              const itemTotal = ((item.quantity * item.unit_cost) * (1 + item.markup_percentage / 100)).toFixed(2);
              const isExpanded = expandedLineItems[item.id];
              return (
                <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`estimate-line-mobile-${index}`}>
                  <div className="overflow-x-auto">
                    <div className="flex w-[calc(100%+84px)]">
                      <button
                        type="button"
                        onClick={() => toggleLineItemExpanded(item.id)}
                        className="w-full flex items-center justify-between px-3 py-3 bg-white"
                        data-testid={`estimate-line-mobile-toggle-${index}`}
                      >
                        <div className="flex-1 text-left min-w-0">
                          <div className="font-semibold text-gray-900 truncate" data-testid={`estimate-line-mobile-title-${index}`}>{itemTitle}</div>
                          <div className="text-xs text-gray-500 truncate" data-testid={`estimate-line-mobile-summary-${index}`}>{itemDetails}</div>
                        </div>
                        <div className="text-right ml-3">
                          <div className="text-sm font-semibold text-gray-900" data-testid={`estimate-line-total-mobile-${index}`}>${itemTotal}</div>
                          <div className="text-xs text-gray-400">{isExpanded ? '▾' : '›'}</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLineItem(index)}
                        className="w-[84px] bg-red-600 text-white flex items-center justify-center text-sm font-semibold"
                        data-testid={`estimate-line-remove-mobile-${index}`}
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="p-3 space-y-3 bg-gray-50">
                      <textarea
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[140px] text-sm"
                        rows="5"
                        placeholder="Item title on first line, details on next line"
                        data-testid={`estimate-line-description-mobile-${index}`}
                      />
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Quantity</label>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            step="0.01"
                            data-testid={`estimate-line-quantity-mobile-${index}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Unit Cost ($)</label>
                          <input
                            type="number"
                            value={item.unit_cost}
                            onChange={(e) => updateLineItem(index, 'unit_cost', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            step="0.01"
                            data-testid={`estimate-line-unit-cost-mobile-${index}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Markup (%)</label>
                          <input
                            type="number"
                            value={item.markup_percentage}
                            onChange={(e) => updateLineItem(index, 'markup_percentage', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            step="0.1"
                            data-testid={`estimate-line-markup-mobile-${index}`}
                          />
                        </div>
                      </div>
                      <div className="text-right text-sm font-semibold text-gray-700" data-testid={`estimate-line-total-detail-mobile-${index}`}>
                        Total: ${itemTotal}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="hidden sm:block space-y-4">
            {formData.line_items.map((item, index) => (
              <div key={item.id} className="border border-gray-300 rounded-lg p-4">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1 space-y-3">
                    <textarea
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[180px]"
                      rows="6"
                      placeholder="Item title on first line, details on next line"
                      data-testid={`estimate-line-description-${index}`}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Quantity</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          step="0.01"
                          data-testid={`estimate-line-quantity-${index}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Unit Cost ($)</label>
                        <input
                          type="number"
                          value={item.unit_cost}
                          onChange={(e) => updateLineItem(index, 'unit_cost', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          step="0.01"
                          data-testid={`estimate-line-unit-cost-${index}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Markup (%)</label>
                        <input
                          type="number"
                          value={item.markup_percentage}
                          onChange={(e) => updateLineItem(index, 'markup_percentage', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          step="0.1"
                          data-testid={`estimate-line-markup-${index}`}
                        />
                      </div>
                    </div>
                    <div className="text-right font-bold text-gray-800" data-testid={`estimate-line-total-${index}`}>
                      Total: ${((item.quantity * item.unit_cost) * (1 + item.markup_percentage / 100)).toFixed(2)}
                    </div>
                  </div>
                  <button
                    onClick={() => removeLineItem(index)}
                    className="w-full lg:w-auto text-red-600 hover:text-red-700 font-semibold border border-red-200 rounded-lg px-3 py-2 text-sm"
                    data-testid={`estimate-line-remove-${index}`}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          {formData.line_items.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No line items yet. Click "Add Line Item" to get started.
            </div>
          )}
        </div>

      <div
        className={`bg-white rounded-lg shadow-sm border-2 p-4 sm:p-6 mb-6 transition-colors ${isDraggingPhotos ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}
        onDrop={handlePhotoDrop}
        onDragOver={handlePhotoDragOver}
        onDragLeave={handlePhotoDragLeave}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800" data-testid="estimate-photos-title">Photos</h3>
            <p className="text-xs text-gray-500" data-testid="estimate-photos-count">{formData.photos?.length || 0} / 20 photos</p>
          </div>
          <label className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 border border-green-600 text-green-700 rounded-lg cursor-pointer hover:bg-green-50 transition-colors">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
              data-testid="estimate-photos-upload"
            />
            {isUploadingPhotos ? 'Uploading...' : 'Add Photos'}
          </label>
        </div>
        {isDraggingPhotos && (
          <div className="flex items-center justify-center py-8 border-2 border-dashed border-blue-400 rounded-lg mb-4 bg-blue-50">
            <p className="text-blue-600 font-medium">Drop photos here to upload</p>
          </div>
        )}
        {isUploadingPhotos && (
          <div className="mb-4">
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 animate-pulse rounded-full" style={{ width: '60%' }}></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Processing images... this may take a moment.</p>
          </div>
        )}
        {!isDraggingPhotos && !isUploadingPhotos && formData.photos?.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-6 border-2 border-dashed border-gray-200 rounded-lg">
            Drag & drop photos here or click "Add Photos" above. Up to 20 images.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {formData.photos.map((photo) => (
              <div key={photo.id} className="relative border border-gray-200 rounded-lg overflow-hidden" data-testid={`estimate-photo-${photo.id}`}>
                <img
                  src={getPhotoUrl(photo.id)}
                  alt={photo.filename}
                  className="w-full h-32 object-cover"
                />
                <button
                  type="button"
                  onClick={() => handleRemovePhoto(photo.id)}
                  className="absolute top-2 right-2 bg-white bg-opacity-90 text-red-600 text-xs px-2 py-1 rounded"
                  data-testid={`estimate-photo-remove-${photo.id}`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

        {/* Totals */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="space-y-3 max-w-md ml-auto">
            <div className="flex justify-between text-lg">
              <span className="text-gray-700">Subtotal:</span>
              <span className="font-semibold" data-testid="estimate-subtotal">${formData.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 text-gray-700">
                <span>Markup:</span>
                <div className="flex border border-gray-300 rounded overflow-hidden text-xs">
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, markup_type: 'percent' }))} className={`px-2 py-0.5 ${(formData.markup_type || 'percent') !== 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="estimate-markup-type-percent">%</button>
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, markup_type: 'flat' }))} className={`px-2 py-0.5 ${formData.markup_type === 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="estimate-markup-type-flat">$</button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={formData.markup_percentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, markup_percentage: parseFloat(e.target.value) || 0 }))}
                  className="w-20 px-2 py-1 border border-gray-300 rounded"
                  step="0.01"
                  data-testid="estimate-markup-percentage"
                />
                <span className="font-semibold" data-testid="estimate-markup-amount">${formData.markup_amount.toFixed(2)}</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 text-gray-700">
                <span>Discount:</span>
                <div className="flex border border-gray-300 rounded overflow-hidden text-xs">
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, discount_type: 'percent' }))} className={`px-2 py-0.5 ${(formData.discount_type || 'percent') !== 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="estimate-discount-type-percent">%</button>
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, discount_type: 'flat' }))} className={`px-2 py-0.5 ${formData.discount_type === 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`} data-testid="estimate-discount-type-flat">$</button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) || 0 }))}
                  className="w-20 px-2 py-1 border border-gray-300 rounded"
                  step="0.01"
                  data-testid="estimate-discount-percentage"
                />
                <span className="font-semibold" data-testid="estimate-discount-amount">-${formData.discount_amount.toFixed(2)}</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Tax (%):</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0, tax_rate_manual_override: true }))}
                  className="w-20 px-2 py-1 border border-gray-300 rounded"
                  step="0.1"
                  data-testid="estimate-tax-rate"
                />
                <span className="font-semibold" data-testid="estimate-tax-amount">${formData.tax_amount.toFixed(2)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span data-testid="estimate-tax-breakdown">
                Province taxes: {(getProvinceTaxSummary(formData.client_province).components || [])
                  .map(component => `${component.name} ${component.rate}%`).join(' + ') || 'N/A'}
              </span>
              <button
                type="button"
                onClick={() => {
                  const { totalRate } = getProvinceTaxSummary(formData.client_province);
                  setFormData(prev => ({ ...prev, tax_rate: totalRate, tax_rate_manual_override: false }));
                }}
                className="text-blue-600 hover:text-blue-700 font-medium"
                data-testid="estimate-tax-use-province"
              >
                Use province rate
              </button>
            </div>
            <div className="border-t-2 border-gray-300 pt-3 flex justify-between text-xl">
              <span className="font-bold text-gray-800">Total (CAD):</span>
              <span className="font-bold text-gray-800" data-testid="estimate-total">${formData.total.toFixed(2)}</span>
            </div>
          </div>
        </div>


      {/* Documents */}
      <div
        className={`bg-white rounded-lg shadow-sm border-2 p-4 sm:p-6 mb-6 transition-colors ${isDraggingDocs ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}
        onDrop={handleDocDrop}
        onDragOver={handleDocDragOver}
        onDragLeave={handleDocDragLeave}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800" data-testid="estimate-docs-title">Documents</h3>
            <p className="text-xs text-gray-500">{formData.documents?.length || 0} / 20 documents</p>
          </div>
          <label className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 border border-green-600 text-green-700 rounded-lg cursor-pointer hover:bg-green-50 transition-colors">
            <input
              type="file"
              multiple
              onChange={handleDocUpload}
              className="hidden"
              data-testid="estimate-docs-upload"
            />
            {isUploadingDocs ? 'Uploading...' : 'Add Documents'}
          </label>
        </div>
        {isDraggingDocs && (
          <div className="flex items-center justify-center py-8 border-2 border-dashed border-blue-400 rounded-lg mb-4 bg-blue-50">
            <p className="text-blue-600 font-medium">Drop documents here to upload</p>
          </div>
        )}
        {isUploadingDocs && (
          <div className="mb-4">
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 animate-pulse rounded-full" style={{ width: '60%' }}></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Uploading documents...</p>
          </div>
        )}
        {!isDraggingDocs && !isUploadingDocs && formData.documents?.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-6 border-2 border-dashed border-gray-200 rounded-lg">
            Drag & drop documents here or click "Add Documents" above. Up to 20 files.
          </div>
        ) : (
          <div className="space-y-2">
            {formData.documents?.map(doc => (
              <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center">
                    {getDocIcon(doc.filename)}
                  </span>
                  <div className="min-w-0">
                    <a
                      href={`${API}/sales/estimates/documents/${doc.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-blue-700 hover:underline truncate block"
                      data-testid={`doc-download-${doc.id}`}
                    >
                      {doc.filename}
                    </a>
                    <span className="text-xs text-gray-400">{formatFileSize(doc.size)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveDocument(doc.id)}
                  className="text-red-400 hover:text-red-600 ml-3 flex-shrink-0 text-lg font-bold"
                  data-testid={`doc-remove-${doc.id}`}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}
      </div>


        {/* Notes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Notes & Contract</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="4"
                placeholder="Public notes visible to client"
                data-testid="estimate-notes"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Contract Terms</label>
                {contractTemplates.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowContractSelector(!showContractSelector)}
                    className="text-sm px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-300 rounded-lg font-medium transition-colors"
                    data-testid="estimate-contract-template-toggle"
                  >
                    {showContractSelector ? 'Hide Templates' : 'Select Templates'}
                  </button>
                )}
              </div>
              
              {/* Contract Template Selector */}
              {showContractSelector && contractTemplates.length > 0 && (
                <div className="mb-3 p-4 border border-gray-300 rounded-lg bg-gray-50">
                  <p className="text-sm text-gray-600 mb-3">
                    Select one or more contract templates to add to your estimate:
                  </p>
                  <div className="space-y-2">
                    {contractTemplates.map(contract => (
                      <label key={contract.id} className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Add contract to the field (append with separator)
                              const separator = formData.contract ? '\n\n---\n\n' : '';
                              setFormData(prev => ({ 
                                ...prev, 
                                contract: prev.contract + separator + contract.content 
                              }));
                            } else {
                              // Remove this contract from the field
                              const updatedContract = formData.contract
                                .split('\n\n---\n\n')
                                .filter(section => section !== contract.content)
                                .join('\n\n---\n\n');
                              setFormData(prev => ({ ...prev, contract: updatedContract }));
                            }
                          }}
                          className="mt-1 w-4 h-4 text-blue-600"
                          data-testid={`estimate-contract-template-${contract.id}`}
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-800">
                            {contract.name}
                            {contract.is_default && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                DEFAULT
                              </span>
                            )}
                          </span>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {contract.content.substring(0, 100)}...
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              <textarea
                value={formData.contract}
                onChange={(e) => setFormData(prev => ({ ...prev, contract: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="8"
                placeholder="Contract terms and conditions"
                data-testid="estimate-contract-text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Private Notes</label>
              <textarea
                value={formData.private_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, private_notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-yellow-50"
                rows="3"
                placeholder="Private notes (not visible to client)"
                data-testid="estimate-private-notes"
              />
            </div>
          </div>
        </div>
        </div>
      </div>

        {showSignatureWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto" data-testid="estimate-signature-warning-modal">
            <div className="bg-white rounded-lg shadow-lg max-w-xl w-full my-auto sm:my-0">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-bold text-gray-800" data-testid="estimate-signature-warning-title">Signed Estimate Detected</h3>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-700" data-testid="estimate-signature-warning-text">
                  This estimate is already signed. To make changes, you must clear signatures or duplicate into a new estimate.
                </p>
                <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
                  <button
                    onClick={handleClearSignaturesAndEdit}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold"
                    data-testid="estimate-signature-warning-clear"
                  >
                    Edit and clear signatures
                  </button>
                  <button
                    onClick={handleDiscardSignedEdit}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold"
                    data-testid="estimate-signature-warning-discard"
                  >
                    Discard changes and continue
                  </button>
                  <button
                    onClick={handleDuplicateAndEdit}
                    className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-semibold"
                    data-testid="estimate-signature-warning-duplicate"
                  >
                    Duplicate and edit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showSendModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto" data-testid="estimate-send-modal">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-lg sm:max-w-6xl sm:h-[80vh] flex flex-col my-auto sm:my-0">
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-bold text-gray-800" data-testid="estimate-send-title">Send Estimate</h3>
                <button
                  onClick={() => setShowSendModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                  data-testid="estimate-send-close"
                >
                  ✕
                </button>
              </div>
              <div className="p-6 space-y-4 flex-1 flex flex-col overflow-hidden">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
                  <input
                    type="email"
                    value={sendForm.recipient_email}
                    onChange={(e) => setSendForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="estimate-send-recipient"
                  />
                </div>
                <div className="flex-1 flex flex-col min-h-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={sendForm.message}
                    onChange={(e) => setSendForm(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg flex-1 resize-none"
                    placeholder="Add a short note for the customer"
                    data-testid="estimate-send-message"
                  />
                <label className="mt-3 flex items-start gap-3 text-sm text-gray-700" data-testid="estimate-send-attach-pdf">
                  <input
                    type="checkbox"
                    checked={sendForm.attach_pdf}
                    onChange={(e) => setSendForm(prev => ({ ...prev, attach_pdf: e.target.checked }))}
                    className="mt-1"
                  />
                  <span>Attach PDF (opening the PDF attachment won’t trigger link-open tracking)</span>
                </label>
                <label className="mt-2 flex items-start gap-3 text-sm text-gray-700" data-testid="estimate-send-cc-admin">
                  <input
                    type="checkbox"
                    checked={sendForm.cc_admin}
                    onChange={(e) => setSendForm(prev => ({ ...prev, cc_admin: e.target.checked }))}
                    className="mt-1"
                  />
                  <span>Send copy to {salesSettings?.company_email || 'admin'}</span>
                </label>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowSendModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
                    data-testid="estimate-send-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEstimate}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                    data-testid="estimate-send-confirm"
                  >
                    Send Estimate
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {previewToken && (
          <HtmlPreviewModal
            title={`Estimate Preview #${previewEstimateNumber || ''}`}
            url={`${window.location.origin}/estimate/${previewToken}?mode=preview`}
            onClose={handleClosePreview}
            testId="estimate-editor-preview-modal"
          />
        )}
        </div>
      </div>
  );
};

export default EstimateEditor;
